import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

/**
 * Mount-anywhere bridge that completes the Public Preview → Login → Path
 * deep-link flow.
 *
 * Why a bridge? AuthContext / AuthCallback / RootGate are pinned per the
 * project's strict-preserve rule. We can't modify them to honour
 * `?resume_path=` directly, so the bridge runs alongside them and
 * intercepts the redirect once auth completes.
 *
 * Flow:
 *   1. Visitor lands on a public preview `/p/{slug}` and clicks
 *      "Unlock the full path" → URL becomes `/login?resume_path={path_id}`.
 *   2. Landing.jsx (and PublicPreview.jsx, defensively) writes
 *      `oak_resume_path={path_id}` to localStorage.
 *   3. User signs in with Google. AuthCallback exchanges, becomes
 *      authenticated, and (per its locked code) navigates to "/".
 *   4. THIS bridge fires once `user` is set: reads the stashed
 *      path_id, clears it, and navigates to `/path/{path_id}` so the
 *      learner lands directly on the full reading pane they wanted.
 */
export default function ResumePathBridge() {
  const { user, initialSessionFetched } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!initialSessionFetched || !user) return;
    let pathId = null;
    try { pathId = localStorage.getItem("oak_resume_path"); } catch {}
    if (!pathId) return;
    // Sanitize — backend path_id is always an alphanumeric+underscore
    // token. Reject anything else to prevent open-redirect abuse via
    // `oak_resume_path=https://evil.com`.
    if (!/^[A-Za-z0-9_-]{6,64}$/.test(pathId)) {
      try { localStorage.removeItem("oak_resume_path"); } catch {}
      return;
    }
    // Clear first so a hard refresh mid-navigation doesn't loop us.
    try { localStorage.removeItem("oak_resume_path"); } catch {}
    // Replace the current entry so the browser's back button doesn't
    // bounce the user back to the login screen post-auth.
    navigate(`/path/${pathId}`, { replace: true });
  }, [initialSessionFetched, user, navigate]);

  return null;
}
